import ApiService from '@core/api/services/ApiService';

// Function to push to the datalayer
export const ga = async (e: DataLayerEvents): Promise<void> => {
  window.dataLayer = window.dataLayer || [];
  console.log(`Pushing Data Layer Event '${e.event}'`, e);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsedData = e as Record<string, any>;

  Object.keys(parsedData).forEach((key) => {
    if (parsedData[key] === '' || parsedData[key] === undefined || (parsedData[key] === null && key !== 'ecommerce')) {
      delete parsedData[key];
    }
  });

  if (e.event === 'form_start' || e.event === 'form_submit') {
    // We need to fetch the signup identifier from the API as the GUID is not useful in the data layer //
    const signupGuid = (e as FormStartEvent | FormSubmitEvent).form_id;
    const request = await ApiService.request<{ name: string }>({
      method: 'GET',
      controller: 'subscription',
      slug: 'get-signup-identifier',
      params: {
        signupGuid,
      },
    });

    const signupIdentifier = request.name;
    if (signupIdentifier) {
      parsedData['form_id'] = signupIdentifier;
    }
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: e.event,
      ...parsedData,
    });
  }
};

type DataLayerEvent =
  | 'request_demo'
  | 'click_social'
  | 'click_to_store_locator'
  | 'file_download'
  | 'store_locator'
  | 'click_to_buy'
  | 'form_start'
  | 'form_submit'
  | 'click_video'
  | 'video_start'
  | 'video_complete'
  | 'video_progress'
  | 'video_download'
  | 'click_language_select'
  | 'view_item_list'
  | 'select_item'
  | 'view_item';

export type DataLayerEvents =
  | RequestDemoEvent
  | ClickSocialEvent
  | ClickToStoreLocatorEvent
  | FileDownloadEvent
  | StoreLocatorEvent
  | ClickToBuyEvent
  | FormStartEvent
  | FormSubmitEvent
  | ClickVideoEvent
  | VideoStartEvent
  | VideoCompleteEvent
  | VideoProgressEvent
  | VideoDownloadEvent
  | ClickLangugeSelectEvent
  | ViewItemListEvent
  | SelectItemEvent
  | ViewItemEvent;

interface BaseDataLayerEvent {
  event: DataLayerEvent;
}

interface RequestDemoEvent extends BaseDataLayerEvent {
  event: 'request_demo';
  item_category: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_name: string;
  item_model: string;
  item_id: string;
}

interface ClickSocialEvent extends BaseDataLayerEvent {
  event: 'click_social';
  cta_name: 'Instagram' | 'YouTube' | 'Facebook' | 'LinkedIn' | 'TikTok' | '';
}

interface ClickToStoreLocatorEvent extends BaseDataLayerEvent {
  event: 'click_to_store_locator';
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_name?: string;
  item_model?: string;
  item_id?: string;
}

export type FileName =
  | 'Attestation of Conformity'
  | 'Declaration of Conformity'
  | 'UKCA certificate'
  | 'CE certificate'
  | 'Product datasheet'
  | 'Size Guide'
  | 'Assumption of Liability'
  | 'Manuals and spare parts'
  | 'Product data A4'
  | 'Product data A4 Landscape'
  | 'Product data A5'
  | 'Fact tag 14.9x6.9'
  | 'Fact tag 15.5x8.5'
  | 'Lighting Plan PDF'
  | 'Other';

export interface FileDownloadEvent extends BaseDataLayerEvent {
  event: 'file_download';
  link_url: string;
  file_name: FileName | 'Other';
  file_extension?: string;
  item_name?: string;
  item_model?: string;
}

interface StoreLocatorEvent extends BaseDataLayerEvent {
  event: 'store_locator';
  event_type: 'contact_number' | 'directions' | 'view_website' | 'search' | 're-search';
  distributor_type?: string;
  search_term?: string;
  store_name?: string;
}

interface ClickToBuyEvent extends BaseDataLayerEvent {
  event: 'click_to_buy';
  item_category: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_name: string;
  item_model: string;
  item_id: string;
}

interface FormStartEvent extends BaseDataLayerEvent {
  event: 'form_start';
  form_id: string;
  form_name: string;
}

interface FormSubmitEvent extends BaseDataLayerEvent {
  event: 'form_submit';
  form_id: string;
  form_name: string;
}

interface ClickVideoEvent extends BaseDataLayerEvent {
  event: 'click_video';
  page_location: string;
  video_title: string;
}

interface VideoStartEvent extends BaseDataLayerEvent {
  event: 'video_start';
  video_duration: number;
  video_percent: string;
  video_provider: string;
  video_title: string;
  video_url: string;
}

interface VideoCompleteEvent extends BaseDataLayerEvent {
  event: 'video_complete';
  video_duration: number;
  video_percent: string;
  video_provider: string;
  video_title: string;
  video_url: string;
}

interface VideoProgressEvent extends BaseDataLayerEvent {
  event: 'video_progress';
  video_current_time: string;
  video_duration: number;
  video_percent: string;
  video_provider: string;
  video_title: string;
  video_url: string;
}

interface VideoDownloadEvent extends BaseDataLayerEvent {
  event: 'video_download';
  page_location: string;
  video_duration: number;
  video_title: string;
}

interface ClickLangugeSelectEvent extends BaseDataLayerEvent {
  event: 'click_language_select';
  element_text?: string;
  langauge_selector_type: 'banner' | 'footer';
  country_language: string;
}

interface ItemListEventItem {
  index?: number;
  item_category: string;
  item_id: number | null;
  item_name: string | null;
  item_model: string | null;
  item_list_name?: string;
}

interface ViewItemListEvent extends BaseDataLayerEvent {
  event: 'view_item_list';
  ecommerce: {
    items: ItemListEventItem[];
  } | null;
}

interface SelectItemEvent extends BaseDataLayerEvent {
  event: 'select_item';
  ecommerce: {
    items: ItemListEventItem[];
  } | null;
}

interface ViewItemEvent extends BaseDataLayerEvent {
  event: 'view_item';
  ecommerce: {
    items: ItemListEventItem[];
  } | null;
}
